// A full white field drawn through WebGL2 (the same path the test uses), fullscreen, with a
// thin grey ring marking the middle, where the lux meter's sensor goes. The ring is drawn
// well outside the sensor (about 15 % of the screen height across) so it does not darken
// what the meter sees. Any key or a click closes it.

import { useEffect, useRef } from "react";

export function WhiteScreen({ onDone, text }: { onDone: () => void; text?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const onDone = () => done.current();
    const el = box.current;
    const c = canvas.current;
    if (!el || !c) return;
    void el.requestFullscreen?.().catch(() => undefined);
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.round(c.clientWidth * dpr);
      c.height = Math.round(c.clientHeight * dpr);
      const gl = c.getContext("webgl2", { alpha: false, antialias: false, preserveDrawingBuffer: true });
      if (!gl) return;
      gl.viewport(0, 0, c.width, c.height);
      gl.clearColor(1, 1, 1, 1);           // background code 1.0: the test's white
      gl.clear(gl.COLOR_BUFFER_BIT);
    };
    draw();
    const finish = () => {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      onDone();
    };
    const onKey = (e: KeyboardEvent) => { e.preventDefault(); finish(); };
    const onFs = () => { if (!document.fullscreenElement) onDone(); };
    window.addEventListener("resize", draw);
    // a short grace period, so the click or key that opened it does not close it
    const t = window.setTimeout(() => {
      window.addEventListener("keydown", onKey);
      el.addEventListener("click", finish);
    }, 400);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", draw);
      window.removeEventListener("keydown", onKey);
      el.removeEventListener("click", finish);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, []);
  return (
    <div ref={box} className="white-screen" role="dialog" aria-label="White screen for the meter">
      <canvas ref={canvas} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
      <div className="white-screen-ring" aria-hidden="true" />
      <div className="white-screen-text">
        {text ?? "Press the meter’s white sensor flat against the middle of the circle. When the number stops changing, remember it, then press any key."}
      </div>
    </div>
  );
}
